import React, { useState } from "react";
import { Modal, Upload, Input, Select, message } from "antd";
import { FileTextOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "react-query";
import useAxiosInstance from "../../lib/useAxiosInstance";

const { Dragger } = Upload;

const AddSirReport = ({ isOpen, onClose }) => {
  const axiosInstance = useAxiosInstance();
  const queryClient = useQueryClient();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [clientIds, setClientIds] = useState([]);
  const [fileList, setFileList] = useState([]);

  const { data: clientData = [] } = useQuery(
    ["get-clients"],
    async () => {
      const res = await axiosInstance.get("api/clients/getAllClients?status=active");
      return res.data?.data || [];
    },
    { refetchOnWindowFocus: false }
  );

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setClientIds([]);
    setFileList([]);
  };

  const handleClose = () => {
    resetForm();
    onClose && onClose();
  };

  const addSirReport = useMutation(
    (formData) =>
      axiosInstance.post("api/powerbi-module/addSirReport", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      }),
    {
      onSuccess: (res) => {
        message.success(res.data?.message || "SIR report added successfully");
        queryClient.invalidateQueries("powerbi-links");
        handleClose();
      },
      onError: (err) => {
        message.error(err.response?.data?.message || "Failed to add SIR report");
      },
    }
  );

  const handleSubmit = () => {
    if (!title.trim()) {
      message.error("Title is required");
      return;
    }
    if (!clientIds.length) {
      message.error("Please select at least one client");
      return;
    }
    if (!fileList.length) {
      message.error("Please upload an HTML file");
      return;
    }

    const formData = new FormData();
    formData.append("title", title);
    formData.append("description", description);
    formData.append("clientIds", JSON.stringify(clientIds));
    formData.append("sirFile", fileList[0].originFileObj || fileList[0]);

    addSirReport.mutate(formData);
  };

  const uploadProps = {
    beforeUpload: () => false,
    fileList,
    accept: ".html,.htm",
    maxCount: 1,
    onChange: ({ fileList: next }) => setFileList(next),
    onRemove: () => setFileList([]),
  };

  return (
    <Modal
      className="share-report-modal edit-reports"
      open={isOpen}
      onCancel={handleClose}
      width={760}
      maskClosable={false}
      okText="Save"
      onOk={handleSubmit}
      confirmLoading={addSirReport.isLoading}
    >
      <div className="modal-body-container">
        <div className="modal-title">
          <h3>Add SIR Report</h3>
        </div>

        <div className="modal-wrapper-body">
          <div className="modal-wrapper-content">
            <div className="add-modal-row">
              <label>Title</label>
              <Input
                placeholder="Enter Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            <div className="add-modal-row">
              <label>Client</label>
              <Select
                mode="multiple"
                className="custom-dropdown"
                style={{ width: "100%" }}
                placeholder="Select one or more clients"
                showSearch
                value={clientIds}
                onChange={setClientIds}
                filterOption={(input, option) =>
                  option?.children?.toString().toLowerCase().includes(input.toLowerCase())
                }
              >
                {clientData.map((client) => (
                  <Select.Option key={client._id} value={client._id}>
                    {`${client.adminFirstName} ${client.adminLastName}`}
                  </Select.Option>
                ))}
              </Select>
            </div>

            <div className="add-modal-row">
              <label>Description</label>
              <Input.TextArea
                placeholder="Enter description (optional)"
                rows={3}
                style={{ resize: "none" }}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={500}
              />
            </div>

            <div className="add-modal-row">
              <label>SIR Report HTML File</label>
              <Dragger {...uploadProps}>
                <p className="ant-upload-drag-icon">
                  <FileTextOutlined />
                </p>
                <p className="ant-upload-text">Click here to upload or drag & drop</p>
                <p className="ant-upload-hint">Only .html/.htm files allowed</p>
              </Dragger>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AddSirReport;
